import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import VerifiedBadge from "../components/VerifiedBadge";
import PitchHeatmap, { resolvePosition } from "../components/PitchHeatmap";
import { Search, X, ArrowLeftRight } from "lucide-react";

async function loadPlayer(id) {
  const [{ data: user }, { data: stats }, { data: fitness }] = await Promise.all([
    api.get(`/users/${id}`),
    api.get(`/users/${id}/stats`),
    api.get(`/users/${id}/fitness`),
  ]);
  return { user, stats, fitness };
}

function PlayerPicker({ side, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      const { data } = await api.get(`/users?q=${encodeURIComponent(q)}&limit=6`);
      setResults(data);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="bg-[#121212] border border-dashed border-[#404040] rounded-sm p-5 h-full">
      <div className="text-xs uppercase tracking-widest text-[#737373] mb-3">
        Player {side === "a" ? "one" : "two"}
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid={`compare-search-${side}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a player…"
          className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none pl-10 pr-3 py-2.5 rounded-sm text-sm text-white transition-colors"
        />
      </div>
      {results.length > 0 && (
        <ul className="mt-2 divide-y divide-[#262626] border border-[#262626] rounded-sm">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                data-testid={`compare-pick-${side}-${u.id}`}
                onClick={() => onPick(u.id)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#1a1a1a] transition-colors"
              >
                <Avatar url={u.avatar_url} name={u.name} size={32} />
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold truncate">
                    {u.name} <VerifiedBadge role={u.role} verified={u.verified} size={11} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] truncate">
                    {u.position || u.role}{u.team ? ` · ${u.team}` : ""}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PlayerHeader({ data, side, onClear }) {
  const { user } = data;
  return (
    <div className="bg-[#121212] border border-[#262626] rounded-sm p-5 relative" data-testid={`compare-player-${side}`}>
      <button
        type="button"
        data-testid={`compare-clear-${side}`}
        onClick={onClear}
        className="absolute top-3 right-3 text-[#737373] hover:text-white"
        aria-label="Remove player"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-4">
        <Link to={`/profile/${user.id}`}>
          <Avatar url={user.avatar_url} name={user.name} size={56} />
        </Link>
        <div className="min-w-0">
          <Link
            to={`/profile/${user.id}`}
            className="font-display text-xl uppercase tracking-tight text-white leading-none hover:underline flex items-center gap-2"
          >
            {user.name}
            <VerifiedBadge role={user.role} verified={user.verified} size={16} />
          </Link>
          <div className="text-xs text-[#a3a3a3] mt-1">
            {user.position || "Player"}{user.team ? ` · ${user.team}` : ""}
          </div>
        </div>
        {resolvePosition(user.position) && (
          <div className="w-14 ml-auto shrink-0 hidden sm:block">
            <PitchHeatmap position={user.position} />
          </div>
        )}
      </div>
    </div>
  );
}

function CompareRow({ label, a, b, unit = "", higherWins = true }) {
  const aNum = typeof a === "number" ? a : null;
  const bNum = typeof b === "number" ? b : null;
  let winner = null;
  if (higherWins !== null && aNum !== null && bNum !== null && aNum !== bNum) {
    winner = aNum > bNum ? "a" : "b";
  }
  const cell = (val, isWinner) => (
    <div
      className={`flex-1 text-center font-display text-2xl leading-none ${
        isWinner ? "text-white" : winner ? "text-[#525252]" : "text-white"
      }`}
    >
      {val ?? "—"}
      {val !== null && val !== undefined && unit && (
        <span className="text-[9px] uppercase tracking-widest text-[#737373] ml-1">{unit}</span>
      )}
    </div>
  );
  return (
    <div
      data-testid={`compare-row-${label.toLowerCase().replaceAll(" ", "-")}`}
      className="flex items-center bg-[#0a0a0a] border border-[#262626] px-4 py-3 rounded-sm"
    >
      {cell(a, winner === "a")}
      <div className="w-32 text-center text-[10px] uppercase tracking-widest text-[#737373] shrink-0">
        {label}
      </div>
      {cell(b, winner === "b")}
    </div>
  );
}

export default function ComparePage() {
  const [params, setParams] = useSearchParams();
  const idA = params.get("a");
  const idB = params.get("b");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);

  const setSlot = (side, id) => {
    const next = Object.fromEntries(params.entries());
    if (id) next[side] = id;
    else delete next[side];
    setParams(next);
  };

  useEffect(() => {
    if (idA) loadPlayer(idA).then(setDataA).catch(() => setSlot("a", null));
    else setDataA(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idA]);

  useEffect(() => {
    if (idB) loadPlayer(idB).then(setDataB).catch(() => setSlot("b", null));
    else setDataB(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idB]);

  const fit = (d, key) => d?.fitness?.averages?.[key]?.avg ?? null;
  const bothReady = dataA && dataB;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="compare-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-2 flex items-center gap-3">
        <ArrowLeftRight className="w-8 h-8" />
        Compare players
      </h1>
      <p className="text-sm text-[#a3a3a3] mb-6">
        Pick two players to see their career, season and fitness numbers side by side.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {dataA ? (
          <PlayerHeader data={dataA} side="a" onClear={() => setSlot("a", null)} />
        ) : (
          <PlayerPicker side="a" onPick={(id) => setSlot("a", id)} />
        )}
        {dataB ? (
          <PlayerHeader data={dataB} side="b" onClear={() => setSlot("b", null)} />
        ) : (
          <PlayerPicker side="b" onPick={(id) => setSlot("b", id)} />
        )}
      </div>

      {bothReady ? (
        <div className="space-y-4" data-testid="compare-results">
          <section>
            <h2 className="font-display text-lg uppercase tracking-widest text-white mb-2">Career</h2>
            <div className="space-y-1.5">
              <CompareRow label="Matches" a={dataA.stats.career.matches} b={dataB.stats.career.matches} />
              <CompareRow label="Goals" a={dataA.stats.career.goals} b={dataB.stats.career.goals} />
              <CompareRow label="Assists" a={dataA.stats.career.assists} b={dataB.stats.career.assists} />
              <CompareRow label="Minutes" a={dataA.stats.career.minutes} b={dataB.stats.career.minutes} />
              <CompareRow label="Followers" a={dataA.user.followers_count} b={dataB.user.followers_count} />
            </div>
          </section>

          <section>
            <h2 className="font-display text-lg uppercase tracking-widest text-white mb-2">
              Fitness (avg per match)
            </h2>
            {[dataA, dataB].every((d) => Object.keys(d.fitness.averages || {}).length === 0) ? (
              <div className="text-sm text-[#737373] bg-[#121212] border border-[#262626] p-5 rounded-sm">
                Neither player has tracker data yet.
              </div>
            ) : (
              <div className="space-y-1.5">
                <CompareRow label="Distance" a={fit(dataA, "distance_km")} b={fit(dataB, "distance_km")} unit="km" />
                <CompareRow label="Top speed" a={fit(dataA, "top_speed_kmh")} b={fit(dataB, "top_speed_kmh")} unit="km/h" />
                <CompareRow label="Sprints" a={fit(dataA, "sprints")} b={fit(dataB, "sprints")} />
                <CompareRow label="Avg HR" a={fit(dataA, "avg_heart_rate")} b={fit(dataB, "avg_heart_rate")} unit="bpm" higherWins={null} />
              </div>
            )}
          </section>

          <section>
            <h2 className="font-display text-lg uppercase tracking-widest text-white mb-2">Latest season</h2>
            <div className="space-y-1.5">
              <CompareRow
                label="Season goals"
                a={dataA.stats.seasons[0]?.goals ?? null}
                b={dataB.stats.seasons[0]?.goals ?? null}
              />
              <CompareRow
                label="Season assists"
                a={dataA.stats.seasons[0]?.assists ?? null}
                b={dataB.stats.seasons[0]?.assists ?? null}
              />
              <CompareRow
                label="Season matches"
                a={dataA.stats.seasons[0]?.matches ?? null}
                b={dataB.stats.seasons[0]?.matches ?? null}
              />
            </div>
            <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-2">
              {dataA.stats.seasons[0]?.season || "—"} vs {dataB.stats.seasons[0]?.season || "—"}
            </p>
          </section>
        </div>
      ) : (
        <div className="text-sm text-[#737373] bg-[#121212] border border-[#262626] p-6 rounded-sm text-center" data-testid="compare-hint">
          Select {dataA || dataB ? "one more player" : "two players"} to start the comparison.
        </div>
      )}
    </div>
  );
}
